"use client"

import { NavLink } from "react-router-dom"
import {
  motion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion"
import { useMemo, useRef } from "react"
import type { RefObject } from "react"
import { Shuffle } from "lucide-react"

import useOrientation from "@/hooks/useOrientation"
import { useIsNativeApp } from "@/hooks/useIsNativeApp"
import TitleS from "@/components/text/title-s"
import TextBoldM from "@/components/text/text-bold-m"
import PlayButton from "@/components/buttons/PlayButton"
import { PLAYLIST } from "@/data"

type playlistHeaderProps = {
  data: {
    id?: string
    title: string
    type: string
    artist: string
    artistImgUrl: string
    imgUrl: string
  }
  duracionTotal: string

  // Opcionales para permitir reutilizar
  // playlistHeader en páginas/copies antiguas.
  isPlaying?: boolean
  isShuffle?: boolean
  onPlay?: () => void
  onShuffle?: () => void

  scrollContainerRef?: RefObject<
    HTMLDivElement | null
  >
}

export default function playlistHeader({
  data,
  duracionTotal,
  isPlaying = false,
  isShuffle = false,
  onPlay,
  onShuffle,
  scrollContainerRef,
}: playlistHeaderProps) {
  const { isPortrait, isLandscape, isStandalone } = useOrientation()
  const isNative = useIsNativeApp()

  const localRef =
    useRef<HTMLDivElement>(null)

  const containerRef =
    scrollContainerRef || localRef

  const { scrollY } = useScroll({
    container: containerRef,
  })

  const rawCoverScale = useTransform(
    scrollY,
    [0, 70, 160, 280, 360],
    [1, 0.985, 0.92, 0.76, 0.60]
  )

  const coverScale = useSpring(
    rawCoverScale,
    {
      stiffness: 280,
      damping: 32,
      mass: 0.35,
    }
  )

  const coverOpacity = useTransform(
    scrollY,
    [0, 100, 220, 340],
    [1, 0.98, 0.70, 0]
  )

  /*
   * La información no necesita otro spring.
   * Un transform directo es mucho más barato.
   */
  const infoOpacity = useTransform(
    scrollY,
    [0, 260, 420],
    [1, 0.98, 0.82]
  )

  /*
   * ============================================================
   * ARTIST
   * Evitamos ejecutar PLAYLIST.find() en cada render.
   */
  const artistLink = useMemo(() => {
    const artist = PLAYLIST.find(
      (a: any) =>
        a?.artist?.toLowerCase?.() ===
        data.artist.toLowerCase()
    )

    return artist
      ? `/artist/${(artist as any).artist}`
      : "#"
  }, [data.artist])

  return (
    <header
      className={`
        relative
        w-full
        select-none md:pb-8
        text-white border-b border-[var(--primary-color)]/10
        ${
          isPortrait
            ? "px-4 pt-20"
            : "px-6 pt-8"
        }
        ${
          isStandalone
            ? "pt-35"
            : ""
        }
      `}
    >
      {/* HERO CONTENT */}
      <div
        className={`
          mx-auto
          flex
          w-full
          max-w-7xl
          ${
            isLandscape
              ? "items-end gap-7"
              : "flex-col items-center text-center"
          }
        `}
      >
        {/* ======================================================
            COVER
            ====================================================== */}
        <motion.div
          style={{
            scale: coverScale,
            opacity: coverOpacity,
            transformOrigin: "center center",
          }}
          className={`
            relative
            z-10
            shrink-0
            overflow-hidden
            rounded-[16px]
            bg-black/20
            shadow-[0_35px_80px_rgba(0,0,0,.42)]
            ${
              isLandscape
                ? "w-[clamp(150px,34dvh,325px)]"
                : "w-[min(74vw,320px)]"
            }
          `}
        >
          <div
            className="
              pointer-events-none
              absolute
              inset-0
              z-10
              rounded-[16px]
              ring-1
              ring-inset
              ring-white/15
            "
          />

          <img
            src={data.imgUrl}
            alt={`${data.title} cover`}
            className="
              block
              aspect-square
              h-auto
              w-full
              object-cover
            "
            draggable={false}
          />
        </motion.div>

        {/* ======================================================
            INFORMATION
            ====================================================== */}
        <motion.div
          style={{
            opacity: infoOpacity,
          }}
          className={`
            relative
            z-20
            min-w-0
            ${
              isLandscape
                ? "flex-1 pb-0 text-left"
                : "mt-7 w-full text-center"
            }
          `}
        >
          <TitleS className="text-[var(--primary-color)]/50 mb-1">{data.type}</TitleS>

          <h1
            className={`
              pointer-events-none
              font-black text-balance
              tracking-[-.045em]
              line-clamp-3
              break-words pb-[.35rem] md:pb-2
            
              ${
                isLandscape
                  ? "mt-0 max-w-[780px] text-[clamp(2.5rem,6vw,3.5rem)] leading-[.96]" //lanscape
                  : "mt-0 text-[clamp(1.35rem,10vw,1.5rem)] leading-[.96]" //portrait
              }
              ${isNative ? "text-[clamp(1.8rem,10vw,2rem)] leading-[.96]" : ""}
              
            `}
          >
            {data.title}
          </h1>


          {/* ARTIST + DURATION */}
          <div
            className={`
              mt-4
              flex
              items-center
              ${
                isLandscape
                  ? "justify-start"
                  : "justify-center"
              }
            `}
          >
            <NavLink
              to={artistLink}
              className="
                flex
                min-w-0
                max-w-[65vw]
                items-center
                gap-2
                rounded-full
                px-1.5
                py-1.5
                pr-3
                transition-transform
                duration-200
                active:scale-95
                lg:hover:scale-[.98]
              "
            >
              <img
                src={data.artistImgUrl}
                alt={data.artist}
                className="
                  h-8
                  w-8
                  shrink-0
                  rounded-full
                  object-cover
                "
                draggable={false}
              />

              <span className="min-w-0 truncate">
                <TextBoldM>
                  {data.artist}
                </TextBoldM>
              </span>
            </NavLink>

            <span className="mx-2 text-white/25">
              •
            </span>

            <span className="shrink-0 text-sm text-white/55">
              {duracionTotal}
            </span>
          </div>

          {/* PRIMARY CONTROLS */}
          {(onPlay || onShuffle) && (
            <div
              className={`
                mt-5
                flex
                items-center
                gap-3
                ${
                  isLandscape
                    ? "justify-start"
                    : "justify-center"
                }
                ${isPortrait ? "pb-4" : ""}
              `}
            >
              {onPlay && (
                <div
                  className="
                    flex
                    h-14
                    w-14
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-[#066eff]
                    shadow-[0_8px_30px_rgba(6,110,255,.35)]
                    transition-transform
                    active:scale-95
                  "
                >
                  <PlayButton
                    active={isPlaying}
                    onPlay={onPlay}
                  />
                </div>
              )}

              {onShuffle && (
                <button
                  type="button"
                  onClick={onShuffle}
                  aria-label="Reproducir aleatoriamente"
                  className={`
                    flex
                    h-14
                    w-14
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    border
                    transition-all
                    active:scale-95
                    ${
                      isShuffle
                        ? "border-blue-400/40 bg-blue-500/15 text-blue-400"
                        : "border-white/15 bg-white/[.07] text-white/80 hover:bg-white/[.12]"
                    }
                  `}
                >
                  <Shuffle
                    size={21}
                    strokeWidth={2}
                  />
                </button>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </header>
  )
}